import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';

import type { Endpoint } from './controlApi';
import { actionsOf, EditConflict, type DraftSchedule } from './scheduleDraft';
import {
  describeRule,
  nthWeekdayLabel,
  scheduleErrorText,
  WEEKDAYS,
  weekdayOf,
  paramText,
  type ScheduleAction,
  type ScheduleRule,
  type StoredSchedule,
} from './scheduleApi';

interface EditorProps {
  open: boolean;
  /** The schedule being edited, or null for a new one. */
  editing: StoredSchedule | null;
  initial: DraftSchedule;
  timezone: string;
  hasLocation: boolean;
  endpoints: Endpoint[];
  onClose: () => void;
  /** Saves; force overwrites a newer version (last write wins, logged). */
  onSave: (data: DraftSchedule, force: boolean) => Promise<void>;
}

/** Create or edit a schedule (SCH-001/002/003/007/010; UI-003). */
export function ScheduleEditor(props: EditorProps) {
  // Remount per schedule/date so the form starts from its initial value.
  return props.open ? <EditorForm {...props} /> : null;
}

function EditorForm({
  editing,
  initial,
  timezone,
  hasLocation,
  endpoints,
  onClose,
  onSave,
}: EditorProps) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [d, setD] = useState<DraftSchedule>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [exclude, setExclude] = useState('');
  const rule = d.rule;
  const setRule = (patch: Partial<ScheduleRule>) => {
    setD((prev) => ({ ...prev, rule: { ...prev.rule, ...patch } }));
  };
  const setAction = (i: number, a: ScheduleAction) => {
    setD((prev) => ({ ...prev, actions: prev.actions.map((x, j) => (j === i ? a : x)) }));
  };

  const holidayMode = rule.holiday_only ? 'only' : rule.exclude_holidays ? 'exclude' : 'ignore';
  const astro = rule.trigger_type === 'astronomical';
  const valid =
    d.name.trim() !== '' &&
    d.actions.length > 0 &&
    d.actions.every((a) => a.endpoint_id && a.action) &&
    (astro ? hasLocation : Boolean(rule.local_time));

  const save = async (force: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await onSave({ ...d, name: d.name.trim() }, force);
      onClose();
    } catch (err) {
      if (err instanceof EditConflict) {
        setConflict(err.message);
      } else {
        setError(scheduleErrorText(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open fullScreen={phone} fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
      <DialogTitle>{editing ? 'Edit schedule' : 'New schedule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }} data-testid="schedule-editor">
          <TextField
            label="Name"
            value={d.name}
            autoFocus
            required
            slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'schedule-name' } }}
            onChange={(e) => {
              setD({ ...d, name: e.target.value });
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={d.enabled}
                onChange={(e) => {
                  setD({ ...d, enabled: e.target.checked });
                }}
              />
            }
            label="Enabled"
          />

          <Divider textAlign="left">When</Divider>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={rule.trigger_type}
            onChange={(_, v: ScheduleRule['trigger_type'] | null) => {
              if (v)
                setRule({
                  trigger_type: v,
                  astro_event: v === 'astronomical' ? 'sunset' : undefined,
                });
            }}
          >
            <ToggleButton value="standard">Clock time</ToggleButton>
            <ToggleButton value="astronomical">Sunrise / sunset</ToggleButton>
          </ToggleButtonGroup>
          {astro && !hasLocation && (
            <Alert severity="warning">
              Set the site&apos;s location first: sunrise and sunset depend on it.
            </Alert>
          )}
          {!astro ? (
            <TextField
              label={`Time (${timezone})`}
              type="time"
              value={rule.local_time ?? ''}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { 'data-testid': 'schedule-time' },
              }}
              onChange={(e) => {
                setRule({ local_time: e.target.value });
              }}
            />
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="astro-event">Event</InputLabel>
                <Select
                  labelId="astro-event"
                  label="Event"
                  value={rule.astro_event ?? 'sunset'}
                  onChange={(e) => {
                    setRule({ astro_event: e.target.value });
                  }}
                >
                  <MenuItem value="dawn">Dawn (civil)</MenuItem>
                  <MenuItem value="sunrise">Sunrise</MenuItem>
                  <MenuItem value="sunset">Sunset</MenuItem>
                  <MenuItem value="dusk">Dusk (civil)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Offset (minutes, − before)"
                type="number"
                value={rule.astro_offset_minutes}
                slotProps={{ htmlInput: { min: -720, max: 720 } }}
                onChange={(e) => {
                  setRule({ astro_offset_minutes: Math.round(Number(e.target.value)) || 0 });
                }}
              />
            </Stack>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Starts"
              type="date"
              value={rule.start_date}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                if (e.target.value) setRule({ start_date: e.target.value });
              }}
            />
            <FormControl fullWidth>
              <InputLabel id="repeat">Repeats</InputLabel>
              <Select
                labelId="repeat"
                label="Repeats"
                value={rule.repeat_pattern}
                inputProps={{ 'data-testid': 'schedule-repeat' }}
                onChange={(e) => {
                  setRule({ repeat_pattern: e.target.value });
                }}
              >
                <MenuItem value="none">Once</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {rule.repeat_pattern !== 'none' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Every"
                type="number"
                value={rule.repeat_interval}
                slotProps={{ htmlInput: { min: 1, max: 99 } }}
                helperText={
                  { daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' }[
                    rule.repeat_pattern
                  ]
                }
                onChange={(e) => {
                  setRule({
                    repeat_interval: Math.min(
                      99,
                      Math.max(1, Math.round(Number(e.target.value)) || 1),
                    ),
                  });
                }}
              />
              <TextField
                fullWidth
                label="Until (optional)"
                type="date"
                value={rule.end_date ?? ''}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => {
                  setRule({ end_date: e.target.value || undefined });
                }}
              />
            </Stack>
          )}

          {rule.repeat_pattern === 'weekly' && (
            <ToggleButtonGroup
              size="small"
              value={
                rule.repeat_weekdays?.length ? rule.repeat_weekdays : [weekdayOf(rule.start_date)]
              }
              onChange={(_, v: number[]) => {
                if (v.length) setRule({ repeat_weekdays: v.sort((a, b) => a - b) });
              }}
              sx={{ flexWrap: 'wrap' }}
              aria-label="Weekdays"
            >
              {WEEKDAYS.map((w, i) => (
                <ToggleButton key={w} value={i} aria-label={w}>
                  {w}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          {rule.repeat_pattern === 'monthly' && (
            <FormControl fullWidth>
              <InputLabel id="monthly-mode">On</InputLabel>
              <Select
                labelId="monthly-mode"
                label="On"
                value={rule.monthly_mode}
                onChange={(e) => {
                  setRule({ monthly_mode: e.target.value });
                }}
              >
                <MenuItem value="day_of_month">
                  Day {Number(rule.start_date.slice(8, 10))} of the month
                </MenuItem>
                <MenuItem value="nth_weekday">
                  The {nthWeekdayLabel(rule.start_date)} {WEEKDAYS[weekdayOf(rule.start_date)]}
                </MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth>
            <InputLabel id="holidays">Holidays</InputLabel>
            <Select
              labelId="holidays"
              label="Holidays"
              value={holidayMode}
              onChange={(e) => {
                const v = e.target.value;
                setRule({ exclude_holidays: v === 'exclude', holiday_only: v === 'only' });
              }}
            >
              <MenuItem value="exclude">Skip on holidays</MenuItem>
              <MenuItem value="only">Only on holidays</MenuItem>
              <MenuItem value="ignore">Run on holidays too</MenuItem>
            </Select>
          </FormControl>

          {rule.repeat_pattern !== 'none' && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Skip a date"
                  type="date"
                  value={exclude}
                  slotProps={{ inputLabel: { shrink: true } }}
                  onChange={(e) => {
                    setExclude(e.target.value);
                  }}
                />
                <Button
                  size="small"
                  disabled={!exclude || rule.excluded_dates.includes(exclude)}
                  onClick={() => {
                    setRule({ excluded_dates: [...rule.excluded_dates, exclude].sort() });
                    setExclude('');
                  }}
                >
                  Add
                </Button>
              </Stack>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {rule.excluded_dates.map((x) => (
                  <Chip
                    key={x}
                    size="small"
                    label={x}
                    onDelete={() => {
                      setRule({ excluded_dates: rule.excluded_dates.filter((y) => y !== x) });
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" data-testid="schedule-summary">
            {describeRule(rule)} ({timezone})
          </Typography>

          <Divider textAlign="left">Do</Divider>
          {endpoints.length === 0 && (
            <Alert severity="info">This site has no lighting endpoints to schedule yet.</Alert>
          )}
          {d.actions.map((a, i) => (
            <ActionRow
              key={i}
              index={i}
              action={a}
              endpoints={endpoints}
              onChange={(next) => {
                setAction(i, next);
              }}
              onRemove={
                d.actions.length > 1
                  ? () => {
                      setD({ ...d, actions: d.actions.filter((_, j) => j !== i) });
                    }
                  : undefined
              }
            />
          ))}
          {endpoints.length > 0 && d.actions.length < 20 && (
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setD({
                    ...d,
                    actions: [
                      ...d.actions,
                      {
                        endpoint_id: endpoints[0]?.id ?? '',
                        action: 'preset.activate',
                        params: {},
                      },
                    ],
                  });
                }}
              >
                Add action
              </Button>
            </Box>
          )}

          <Divider textAlign="left">If the appliance was off</Divider>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="misfire">Late runs</InputLabel>
              <Select
                labelId="misfire"
                label="Late runs"
                value={d.misfire.policy}
                onChange={(e) => {
                  const policy = e.target.value;
                  setD({
                    ...d,
                    misfire:
                      policy === 'skip'
                        ? { policy }
                        : { policy, within_minutes: d.misfire.within_minutes ?? 15 },
                  });
                }}
              >
                <MenuItem value="fire_if_within">Run late, if within…</MenuItem>
                <MenuItem value="skip">Skip</MenuItem>
              </Select>
            </FormControl>
            {d.misfire.policy === 'fire_if_within' && (
              <TextField
                fullWidth
                label="Minutes"
                type="number"
                value={d.misfire.within_minutes ?? 15}
                slotProps={{ htmlInput: { min: 1, max: 1440 } }}
                onChange={(e) => {
                  setD({
                    ...d,
                    misfire: {
                      policy: 'fire_if_within',
                      within_minutes: Math.min(
                        1440,
                        Math.max(1, Math.round(Number(e.target.value)) || 1),
                      ),
                    },
                  });
                }}
              />
            )}
          </Stack>
          <TextField
            label="Priority (lower runs first at the same time)"
            type="number"
            value={d.priority}
            slotProps={{ htmlInput: { min: 0, max: 1000 } }}
            onChange={(e) => {
              setD({
                ...d,
                priority: Math.min(1000, Math.max(0, Math.round(Number(e.target.value)) || 0)),
              });
            }}
          />

          {error && (
            <Alert severity="error" data-testid="schedule-error">
              {error}
            </Alert>
          )}
          {conflict && (
            <Alert
              severity="warning"
              data-testid="schedule-conflict"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    void save(true);
                  }}
                >
                  Save anyway
                </Button>
              }
            >
              {conflict} Saving replaces their version; it stays in the history and can be restored.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!valid || busy}
          data-testid="schedule-save"
          onClick={() => {
            void save(false);
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ActionRow({
  index,
  action,
  endpoints,
  onChange,
  onRemove,
}: {
  index: number;
  action: ScheduleAction;
  endpoints: Endpoint[];
  onChange: (a: ScheduleAction) => void;
  onRemove?: () => void;
}) {
  const ep = endpoints.find((e) => e.id === action.endpoint_id);
  const specs = actionsOf(ep);
  const spec = specs.find((s) => s.name === action.action);
  const params = action.params as Record<string, unknown>;
  const setParam = (name: string, value: unknown) => {
    const next = { ...params };
    onChange({
      ...action,
      params:
        value === '' || value === undefined
          ? Object.fromEntries(Object.entries(next).filter(([k]) => k !== name))
          : { ...next, [name]: value },
    });
  };

  return (
    <Stack spacing={1.5} data-testid={`schedule-action-${String(index)}`}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' } }}
      >
        <FormControl fullWidth size="small">
          <InputLabel id={`ep-${String(index)}`}>Endpoint</InputLabel>
          <Select
            labelId={`ep-${String(index)}`}
            label="Endpoint"
            value={ep ? action.endpoint_id : ''}
            onChange={(e) => {
              onChange({ endpoint_id: e.target.value, action: action.action, params: {} });
            }}
          >
            {endpoints.map((x) => (
              <MenuItem key={x.id} value={x.id}>
                {x.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id={`act-${String(index)}`}>Action</InputLabel>
          <Select
            labelId={`act-${String(index)}`}
            label="Action"
            value={spec ? action.action : ''}
            onChange={(e) => {
              onChange({ ...action, action: e.target.value, params: {} });
            }}
          >
            {specs.map((s) => (
              <MenuItem key={s.name} value={s.name}>
                {s.label ?? s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {onRemove && (
          <IconButton
            aria-label="Remove action"
            onClick={onRemove}
            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        )}
      </Stack>
      {spec && spec.params.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {spec.params
            .filter((p) => p.type !== 'rgb')
            .map((p) => {
              const items = p.ref ? (ep?.state?.inventory[p.ref] ?? []) : [];
              const label = `${p.label ?? p.name}${p.required ? ' *' : ''}`;
              const value = params[p.name];
              if (p.type === 'id' && items.length > 0) {
                return (
                  <FormControl key={p.name} fullWidth size="small">
                    <InputLabel id={`${p.name}-${String(index)}`}>{label}</InputLabel>
                    <Select
                      labelId={`${p.name}-${String(index)}`}
                      label={label}
                      value={typeof value === 'string' ? value : ''}
                      onChange={(e) => {
                        setParam(p.name, e.target.value);
                      }}
                    >
                      {items.map((it) => (
                        <MenuItem key={it.id} value={it.id}>
                          {it.name || it.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }
              const numeric = p.type === 'integer' || p.type === 'number';
              return (
                <TextField
                  key={p.name}
                  fullWidth
                  size="small"
                  label={label}
                  type={numeric ? 'number' : 'text'}
                  value={paramText(value)}
                  slotProps={{
                    htmlInput: {
                      min: p.min,
                      max: p.max,
                      'data-testid': `param-${p.name}-${String(index)}`,
                    },
                  }}
                  onChange={(e) => {
                    const v = e.target.value;
                    setParam(p.name, numeric ? (v === '' ? '' : Number(v)) : v);
                  }}
                />
              );
            })}
        </Stack>
      )}
    </Stack>
  );
}
